// Э-5. График цикла: та же сетка, что в Excel (захватки × работы × дни), поверх плана — факт из отчётов.
// В режиме «30 дней» видны несколько перекрывающихся циклов.

import { useState } from "react";
import { addDays, fmtDate, fmtNum } from "../../shared/format";
import { useLive } from "../../shared/live";
import { setQuery, useRoute } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Field, Input, Legend, Loading, Modal, Select, useAsync, useToast } from "../../shared/ui";
import { api, can, floorAbbr, floorName, GANTT_LEGEND, live, useCtx } from "../ctx";

export function SchedulePage({ id }: { id: string }) {
  const { me } = useCtx();
  const r = useRoute();
  const mode = r.query.get("mode") || "cycle";
  const floors = useAsync<any[]>(() => api.get(`/buildings/${id}/floors`), [id]);
  const current = floors.data?.find((f) => f.status !== "done")?.cycle;
  const cycle = Number(r.query.get("cycle") || current || 1);
  const g = useAsync<any>(() => (mode === "cycle" ? api.get(`/buildings/${id}/schedule`, { cycle }) : api.get(`/buildings/${id}/schedule`)), [id, mode, cycle, !!floors.data]);
  const [shift, setShift] = useState(false);
  useLive(live, ["report.", "cycle."], () => g.reload(), 1000);
  const shifter = can(me, "plan.shift");
  const proposer = !shifter && can(me, "plan.request");
  const cyc = g.data?.cycles?.[0];
  return (
    <>
      <TopBar title={mode === "cycle" && cyc ? `${floorName(cyc.floor)}${g.data?.days.find((d: any) => d.today)?.cycle_day ? ` · день ${g.data.days.find((d: any) => d.today).cycle_day}` : ""}` : "График: 30 дней"}>
        <Select aria-label="Режим" value={mode} onChange={(e) => setQuery({ mode: e.target.value === "cycle" ? null : e.target.value })}
          options={[{ value: "cycle", label: "Один этаж" }, { value: "range", label: "30 дней" }]} style={{ width: 150 }} />
        {mode === "cycle" && floors.data && (
          <Select aria-label="Этаж" value={String(cycle)} onChange={(e) => setQuery({ cycle: e.target.value })} style={{ width: 130 }}
            options={floors.data.map((f) => ({ value: String(f.cycle), label: floorName(f.number) }))} />
        )}
        {mode === "cycle" && shifter && cyc && <Button onClick={() => setShift(true)}>Сдвинуть план</Button>}
        {mode === "cycle" && proposer && cyc && <Button onClick={() => setShift(true)}>Предложить сдвиг</Button>}
      </TopBar>
      <div className="content">
        {!g.data ? <Loading error={g.error} onRetry={g.reload} /> : (
          <div className="panel">
            <div className="pb gantt"><Gantt data={g.data} multi={mode !== "cycle"} /></div>
            <div className="pb row between">
              <div className="fills"><Legend items={[...GANTT_LEGEND, { cls: "sw-none", label: "Пунктир — прогноз" }]} /></div>
              <span className="small muted">Голубой столбец — сегодня{mode === "cycle" ? ", штриховка — дни после планового окончания этажа" : ""}. А — арматурщики, М — монтажники, П — плотники</span>
            </div>
          </div>
        )}
      </div>
      {shift && cyc && <ShiftDialog buildingId={id} cycle={cyc} manager={shifter} onClose={() => setShift(false)} onDone={() => { setShift(false); g.reload(); floors.reload(); }} />}
    </>
  );
}

const COL = 34; // ширина дня, px (как в Excel-графике заказчика)

/** Сетка «работы × дни» как в Excel: сплошные полосы. Серое — план, поверх — факт цветом статуса,
 * пунктир — прогноз оставшегося. Выполненное — зелёным, даже если с опозданием (опоздание видно по
 * выходу полосы за серый план и в подсказке). */
function Gantt({ data, multi }: { data: any; multi: boolean }) {
  const days: any[] = data.days;
  const today: string = days.find((x) => x.today)?.date || "";
  const idx = (d: string) => days.findIndex((x) => x.date === d);
  const first = days[0]?.date || "";
  const last = days[days.length - 1]?.date || "";
  const span = (a: string, b: string) => {
    if (!a || !b || b < first || a > last) return null;
    const i = a < first ? 0 : idx(a);
    const j = b > last ? days.length - 1 : idx(b);
    return i < 0 || j < i ? null : { left: i * COL + 2, width: (j - i + 1) * COL - 4 };
  };
  const planEnd = !multi && data.cycles?.[0]?.planned_end;
  const lastWork = [...days].reverse().find((d) => d.date <= today && d.workday)?.date || today;
  let lastGroup = "";
  return (
    <table>
      <thead>
        <tr>
          <th style={{ textAlign: "left", padding: "0 10px", position: "sticky", left: 0, background: "var(--panel)", zIndex: 2 }}>Работа</th>
          <th className="muted small">Бр.</th>
          <th className="track-h" style={{ width: days.length * COL }}>
            <div className="days">
              {days.map((d) => (
                <span key={d.date} className={`${d.today ? "today" : ""}${planEnd && d.date > planEnd ? " over" : ""}${d.workday ? "" : " off"}`} title={`${fmtDate(d.date)}${d.today ? " — сегодня" : ""}`}>
                  {multi ? Number(d.date.slice(8)) : d.cycle_day ?? ""}
                </span>
              ))}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {data.rows.map((t: any) => {
          const group = (multi ? `${t.floor}` : "") + t.grip[0];
          const sep = lastGroup && group !== lastGroup;
          lastGroup = group;
          const plan = span(t.planned_start, t.planned_end);
          const factEnd = t.fact_end || (t.status !== "done" ? lastWork : t.fact_start);
          const fact = t.fact_start ? span(t.fact_start, factEnd < t.fact_start ? t.fact_start : factEnd) : null;
          const lateNotStarted = !t.fact_start && t.status !== "done" && t.planned_start <= today;
          const factCls = t.status === "done" ? "done" : t.deviation === "critical" ? "critical" : t.deviation === "behind" ? "behind" : "in_progress";
          const fcFrom = forecastFrom(t, today);
          const fc = t.status !== "done" && t.forecast_end && t.forecast_end > (fact ? factEnd : today) ? span(fcFrom, t.forecast_end) : null;
          const lateDays = t.status === "done" && t.fact_end && t.fact_end > t.planned_end;
          const tip = `${t.grip} · ${t.work}: план ${fmtDate(t.planned_start)}–${fmtDate(t.planned_end)}`
            + (t.fact_start ? `, факт с ${fmtDate(t.fact_start)}${t.fact_end ? ` по ${fmtDate(t.fact_end)}` : ""}` : "")
            + `, выполнено ${fmtNum(t.fact_percent, 0)}% (по плану ${fmtNum(t.planned_percent, 0)}%)`
            + (lateDays ? " — закончили позже плана" : "")
            + (t.forecast_end && t.status !== "done" ? `, прогноз окончания ${fmtDate(t.forecast_end)}` : "");
          return (
            <tr key={`${t.cycle}-${t.key}`} className={sep ? "sep" : ""}>
              <td className="name">{multi && <span className="muted">{floorAbbr(t.floor)} · </span>}{t.grip} · {t.work}</td>
              <td className="crew">{t.crew === "М" && t.work_code === "slab_concrete" ? "М+П" : t.crew}</td>
              <td className="gtrack" style={{ width: days.length * COL }} title={tip}>
                {days.map((d, i) => (!d.workday || d.today || (planEnd && d.date > planEnd))
                  ? <span key={d.date} className={`stripe${d.today ? " today" : d.workday ? " over" : " off"}`} style={{ left: i * COL, width: COL }} /> : null)}
                {plan && <div className={`bar ${lateNotStarted ? (t.deviation === "critical" ? "critical" : "behind") : "plan"}`} style={plan} />}
                {fact && <div className={`bar ${factCls}`} style={fact}>{t.status !== "done" && fact.width > 40 ? <span>{fmtNum(t.fact_percent, 0)}%</span> : null}</div>}
                {fc && <div className="bar forecast" style={fc} />}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ShiftDialog({ buildingId, cycle, manager, onClose, onDone }: { buildingId: string; cycle: any; manager: boolean; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [days, setDays] = useState("1");
  const [scope, setScope] = useState("this_and_next");
  const [reason, setReason] = useState("");
  return (
    <Modal title={manager ? `Сдвиг плана: ${floorName(cycle.floor, false)}` : `Предложение сдвига: ${floorName(cycle.floor, false)}`} onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={reason.trim().length < 2 || !Number(days)} onClick={async () => {
        try {
          if (manager) {
            const g = await api.get(`/buildings/${buildingId}/grid`);
            const row = g.rows.find((r: any) => r.cycle === cycle.number);
            await api.post(`/cycles/${row.cycle_id}/shift`, { days: Number(days), scope, reason });
            toast.ok("План сдвинут, прежняя версия сохранена");
          } else {
            await api.post(`/buildings/${buildingId}/plan-requests`, { cycle_number: cycle.number, shift_days: Number(days), scope, reason });
            toast.ok("Предложение отправлено руководителю");
          }
          onDone();
        } catch (e) { toast.error(e); }
      }}>{manager ? "Сдвинуть" : "Отправить"}</Button>
    </>}>
      <div className="form-grid">
        <Field label="На сколько рабочих дней" hint="Отрицательное число — раньше"><Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} /></Field>
        <Field label="Что сдвинуть"><Select value={scope} onChange={(e) => setScope(e.target.value)} options={[{ value: "this", label: "Только этот этаж" }, { value: "this_and_next", label: "Этот и все следующие" }]} /></Field>
        <Field label="Причина" wide><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Например: кран на ремонте" /></Field>
      </div>
    </Modal>
  );
}

/** Начало прогнозного отрезка: идущая работа продолжается с завтра, не начатая занимает свою длительность до прогноза. */
function forecastFrom(t: any, today: string): string {
  if (t.forecast_start) return t.forecast_start;
  if (t.status === "in_progress") return addDays(today, 1);
  const dur = Math.round((Date.parse(t.planned_end) - Date.parse(t.planned_start)) / 86400000);
  const s = addDays(t.forecast_end, -dur);
  return s > today ? s : addDays(today, 1);
}
